import { invoiceOutstandingMinor } from "../../shared/invoice-settlement";
import { invoiceStatus } from "../pages/InvoiceActions";
import { formatMoney, type InvoiceDraftRecord } from "../pages/invoiceApi";
import {documentWorkSummary} from "./documentPresentation";

export type RegisterKind = "invoice" | "estimate";
export type RegisterScope = "active" | "history";
export type RegisterSortKey = "client" | "description" | "issued" | "due" | "status" | "amount" | "updated";
export interface RegisterSort { key: RegisterSortKey; direction: "ascending" | "descending" }
export function registerFilterOptions(kind:RegisterKind,scope:RegisterScope) {
  return scope==="history" ? kind==="invoice" ? [{value:"all",label:"All"},{value:"archived",label:"Archived"}] : [{value:"all",label:"All"},{value:"converted",label:"Converted"},{value:"archived",label:"Archived"}]
    : kind==="invoice" ? [{value:"all",label:"All"},{value:"draft",label:"Drafts"},{value:"outstanding",label:"Outstanding"},{value:"overdue",label:"Overdue"},{value:"paid",label:"Paid"}] : [{value:"all",label:"All"},{value:"draft",label:"Drafts"},{value:"issued",label:"Issued"},{value:"sent",label:"Sent"}];
}
export function registerNavigation(search:string,kind:RegisterKind) {
  const params=new URLSearchParams(search);
  if(!params.has("scope")&&!params.has("filter"))return null;
  const scope:RegisterScope=params.get("scope")==="history"?"history":"active";
  const requested=params.get("filter")??"all";
  return {scope,filter:registerFilterOptions(kind,scope).some(item=>item.value===requested)?requested:"all",query:params.get("query")??""};
}

export function registerSortNavigation(search: string): RegisterSort | null {
  const params = new URLSearchParams(search), key = params.get("sort"), direction = params.get("direction");
  return ["client", "description", "issued", "due", "status", "amount", "updated"].includes(key ?? "") && ["ascending", "descending"].includes(direction ?? "")
    ? { key: key as RegisterSortKey, direction: direction as RegisterSort["direction"] } : null;
}

const collator = new Intl.Collator("en-US", { numeric: true, sensitivity: "base" });
const searchDateFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
function searchDate(value: string): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(date.valueOf()) ? "" : searchDateFormat.format(date);
}

export function registerStatus(record: InvoiceDraftRecord, kind: RegisterKind): string {
  if (kind === "invoice") return invoiceStatus(record);
  if (record.convertedInvoiceId) return "Converted";
  if (record.archivedAt) return "Archived";
  return record.documentState === "draft" ? "Draft" : record.sentAt ? "Sent" : "Issued";
}

export function registerScope(record: InvoiceDraftRecord, kind: RegisterKind): RegisterScope {
  return kind === "invoice" ? record.archivedAt ? "history" : "active" : record.convertedInvoiceId || record.archivedAt ? "history" : "active";
}

export function matchesRegisterFilter(record: InvoiceDraftRecord, kind: RegisterKind, filter: string): boolean {
  if (filter === "all") return true;
  const status = registerStatus(record, kind);
  if (kind === "invoice" && filter === "outstanding") return record.documentState === "issued" && !record.archivedAt && invoiceOutstandingMinor(record) > 0;
  return (status.startsWith("Paid") ? "paid" : status.toLowerCase()) === filter;
}

export function matchesRegisterSearch(record: InvoiceDraftRecord, kind: RegisterKind, query: string): boolean {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return true;
  return [record.clientName ?? "Client not selected", record.number ?? "Continue draft", documentWorkSummary(record.lines).fullText, registerStatus(record, kind), searchDate(record.issueDate), searchDate(record.dueDate), formatMoney(record.grandTotalMinor), ...record.lines.map(line => line.description)].some(value => value.toLocaleLowerCase().includes(normalized));
}

export function sortRegisterRecords(records: InvoiceDraftRecord[], kind: RegisterKind, sort: RegisterSort): InvoiceDraftRecord[] {
  const value = (record: InvoiceDraftRecord): string | number => {
    switch (sort.key) {
      case "client": return record.clientName ?? "";
      case "description": return documentWorkSummary(record.lines).sortText;
      case "issued": return record.documentState === "issued" ? record.issueDate : "";
      case "due": return record.dueDate ?? "";
      case "status": return registerStatus(record, kind);
      case "amount": return record.grandTotalMinor;
      case "updated": return Date.parse(record.updatedAt);
    }
  };
  const direction = sort.direction === "ascending" ? 1 : -1;
  return records.map((record, index) => ({ record, index })).sort((a, b) => {
    const first = value(a.record), second = value(b.record);
    const comparison = typeof first === "number" && typeof second === "number" ? first - second : collator.compare(String(first), String(second));
    return comparison * direction || (sort.key === "updated" ? a.record.id.localeCompare(b.record.id) : a.index - b.index);
  }).map(({ record }) => record);
}

export function invoiceRegisterSummary(records: InvoiceDraftRecord[], today = new Date().toISOString().slice(0, 10)) {
  let onTimeMinor = 0, overdueMinor = 0, collectedLast30Minor = 0, paidInvoiceCount = 0;
  for (const record of records) {
    const received = record.receivedLast30Minor ?? 0;
    collectedLast30Minor += received;
    if (received > 0) paidInvoiceCount += 1;
    if (record.archivedAt || record.documentState !== "issued") continue;
    const outstanding = invoiceOutstandingMinor(record);
    if (record.dueDate < today) overdueMinor += outstanding;
    else onTimeMinor += outstanding;
  }
  return { onTimeMinor, overdueMinor, comingInMinor: onTimeMinor + overdueMinor, collectedLast30Minor, paidInvoiceCount };
}

export function billAgainSources(records: InvoiceDraftRecord[], activeClientIds: Set<string>): InvoiceDraftRecord[] {
  const clients = new Set<string>();
  return [...records].filter(record => record.documentState === "issued" && !record.archivedAt && record.clientId && activeClientIds.has(record.clientId)).sort((a, b) => (b.sentAt ?? b.issueDate).localeCompare(a.sentAt ?? a.issueDate)).filter(record => {
    if (clients.has(record.clientId!)) return false;
    clients.add(record.clientId!);
    return true;
  }).slice(0, 3);
}
